import { useMemo } from 'react';

// Import types
import type { EventByUser } from '../../types/schedule';

export type UseOverlapTimesParams = {
  users: string[];
  eventsByUser: EventByUser;
  actualYmds: string[]; // list of YMDs rendered as columns
  TIMES: string[]; // quarter-hour labels for rows
};

export type OverlapTimesResult = {
  countsByDateKey: Record<string, number[]>; // dateKey -> counts per row
  totalEligible: number; // number of users with any availability
};

/**
 * Derives per-cell availability counts and the denominator for the "overlapping times" overlay.
 *
 * Behavior parity note:
 * - This uses the same simple per-cell check as the previous inline implementation
 *   (matching on stored `dateKey` and index range), intentionally avoiding timezone
 *   conversions to stay consistent with the current UI.
 */
export default function useOverlapTimes(
  params: UseOverlapTimesParams,
): OverlapTimesResult {
  const { users, eventsByUser, actualYmds, TIMES } = params;

  const result = useMemo<OverlapTimesResult>(() => {
    // Helper: ymd -> dateKey (stable JS Date string)
    const dateKeyFromYmd = (ymd: string): string => {
      const [y, m, d] = ymd.split('-').map(Number);
      return new Date(y, m - 1, d).toDateString();
    };

    // Initialize typed arrays for each visible dateKey
    const dateKeys = actualYmds.map(dateKeyFromYmd);
    const countsMap = new Map<string, Int16Array>();
    for (const dk of dateKeys) countsMap.set(dk, new Int16Array(TIMES.length));

    // Denominator: users with any availability
    let totalEligible = 0;

    // Accumulate counts per cell using typed arrays
    for (const u of users) {
      const list = eventsByUser[u] || [];
      if (list.length === 0) continue;
      totalEligible += 1;
      for (const seg of list) {
        const arr = countsMap.get(seg.dateKey);
        if (!arr) continue; // segment for a date not currently visible
        const start = Math.max(0, seg.startIndex);
        const end = Math.min(TIMES.length, Math.max(start, seg.endIndex));
        for (let r = start; r < end; r++) arr[r] += 1;
      }
    }

    // Convert back to a plain object of number arrays for the grid
    const countsByDateKey: Record<string, number[]> = {};
    for (const [dk, arr] of countsMap.entries()) {
      // Spread/Array.from to make a real number[]
      countsByDateKey[dk] = Array.from(arr);
    }

    return {
      countsByDateKey,
      totalEligible,
    };
  }, [users, eventsByUser, actualYmds, TIMES.length]);

  return result;
}
